import React, { useState, useEffect, useRef } from 'react';
import '../css/GameChat.css';
import socket from '../utils/socket';

const GameChat = ({ gameId, playerName, showChat, setShowChat, unreadCount, setUnreadCount }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const audioRef = useRef(null);
  
  // Track sent message IDs to prevent duplicates
  const sentMessageIds = useRef(new Set());

  // List of inappropriate words to filter out
  const profanityList = [
    // English
    'fuck', 'shit', 'ass', 'bitch', 'cunt', 'damn', 'dick', 'pussy', 'asshole',
    'bastard', 'whore', 'slut', 'cock', 'piss', 'crap', 'twat', 'wanker',
    
    // Dutch
    'kut', 'lul', 'klootzak', 'eikel', 'hoer', 'slet', 'neuk', 'tyfus',
    'godverdomme', 'kanker', 'teef', 'mongool', 'trut',
    
    // German
    'scheiße', 'arschloch', 'hurensohn', 'fotze', 'hure', 'wichser',
    'schwanz', 'fick', 'schlampe', 'schwuchtel',
    
    // French
    'putain', 'merde', 'connard', 'salope', 'enculé', 'bite', 'couilles',
    'foutre', 'con', 'pute', 'cul', 'branleur',
    
    // Spanish
    'joder', 'mierda', 'puta', 'cabrón', 'coño', 'gilipollas',
    'hijoputa', 'capullo', 'maricón', 'cojones',
    
    // Italian
    'cazzo', 'stronzo', 'figa', 'puttana', 'vaffanculo', 'troia',
    'minchia', 'culo', 'bastardo'
  ];

  // Function to check if a message contains profanity
  const containsProfanity = (message) => {
    if (!message) return false;
    
    const lowerCaseMsg = message.toLowerCase();
    
    // Check if the message contains any word from the profanity list
    return profanityList.some(word => {
      // Check for the word as a whole or part of another word
      const regex = new RegExp(`\\b${word}\\b|${word}`, 'i');
      return regex.test(lowerCaseMsg);
    });
  };

  // Function to censor profanity in a message
  const censorMessage = (message) => {
    if (!message) return '';
    
    let censoredMessage = message;
    profanityList.forEach(word => {
      // Replace each occurrence of the profanity with asterisks
      const regex = new RegExp(word, 'gi');
      censoredMessage = censoredMessage.replace(regex, '*'.repeat(word.length));
    });
    
    return censoredMessage;
  };

  useEffect(() => {
    if (!socket) return;

    // Request chat history when component mounts
    socket.emit('chat:getHistory', { gameId });
    
    // Set a timeout to reset isLoading if no response is received
    const historyTimeout = setTimeout(() => {
      setIsLoading(false);
      console.log('Chat history request timed out, showing empty state');
    }, 5000);

    const handleNewMessage = (message) => {
      // Check if this is our own message that we already displayed
      const messageKey = `${message.playerName}:${message.message}:${message.timestamp.substring(0, 16)}`;
      
      // If we sent this message AND we've already added it to our local messages, don't add it again
      if (message.playerName === playerName && sentMessageIds.current.has(messageKey)) {
        console.log('Ignoring duplicate message:', messageKey);
        return;
      }
      
      // Add to messages
      setMessages(prevMessages => [...prevMessages, message]);
      
      // If chat is not visible, increment unread count and play sound
      if (!showChat) {
        setUnreadCount(prev => prev + 1);
        // Play notification sound if the message is not from current user
        if (message.playerName !== playerName && audioRef.current) {
          audioRef.current.play().catch(err => console.error("Error playing sound:", err));
        }
      }
    };

    const handleChatHistory = ({ chatHistory }) => {
      clearTimeout(historyTimeout); // Clear timeout since we got a response
      console.log(`Received chat history with ${chatHistory?.length || 0} messages`);
      setMessages(chatHistory || []);
      setIsLoading(false);
    };

    socket.on('chat:newMessage', handleNewMessage);
    socket.on('chat:history', handleChatHistory);

    return () => {
      clearTimeout(historyTimeout); // Clear timeout on cleanup
      socket.off('chat:newMessage', handleNewMessage);
      socket.off('chat:history', handleChatHistory);
    };
  }, [gameId, showChat, setUnreadCount, playerName]);

  // Scroll to bottom when messages change or chat becomes visible
  useEffect(() => {
    if (showChat && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, showChat]);

  // Focus input when chat becomes visible
  useEffect(() => {
    if (showChat && messageInputRef.current) {
      messageInputRef.current.focus();
    }
    
    // Reset unread count when opening chat
    if (showChat && unreadCount > 0) {
      setUnreadCount(0);
    }
  }, [showChat, unreadCount, setUnreadCount]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !socket) return;

    try {
      // Check if message contains profanity
      const hasProfanity = containsProfanity(newMessage.trim());
      
      // If message contains profanity, either censor it or reject it
      let processedMessage = newMessage.trim();
      if (hasProfanity) {
        // Option 1: Censor the message
        processedMessage = censorMessage(processedMessage);
        
        // Option 2: Reject the message entirely (uncomment to use this approach instead)
        // alert("Your message contains inappropriate language and cannot be sent.");
        // return;
      }
      
      // Play sound for sent message
      if (audioRef.current) {
        // Clone the audio element to allow playing sound in rapid succession
        const soundClone = audioRef.current.cloneNode(true);
        soundClone.volume = 0.5; // Reduce volume for sent messages
        soundClone.play().catch(err => console.error("Error playing sound:", err));
      }
      
      const timestamp = new Date().toISOString();
      const messageKey = `${playerName}:${processedMessage}:${timestamp.substring(0, 16)}`;
      
      // Track this message to prevent duplication
      sentMessageIds.current.add(messageKey);
      
      // Log the message we're tracking
      console.log('Added message to tracking:', messageKey);
      
      // Create a local message and add it to UI immediately
      const localMessage = {
        id: `local-${Date.now()}`,
        playerName,
        message: processedMessage,
        timestamp
      };
      
      // Add the message to our local state
      setMessages(prevMessages => [...prevMessages, localMessage]);
      
      // Send to server
      socket.emit('chat:message', {
        gameId,
        playerName,
        message: processedMessage
      });
      
      // Clear input
      setNewMessage('');
      
      // Clean up old tracked messages (keep only last 50)
      if (sentMessageIds.current.size > 50) {
        const toRemove = Array.from(sentMessageIds.current).slice(0, sentMessageIds.current.size - 50);
        toRemove.forEach(key => sentMessageIds.current.delete(key));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Show a temporary error state
      setTimeout(() => alert('Failed to send message. Please try again.'), 100);
    }
  };

  const formatTimestamp = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const toggleChat = () => {
    setShowChat(!showChat);
    if (!showChat) {
      setUnreadCount(0);
    }
  };

  return (
    <div className="game-chat-container">
      {/* Audio element for notification sound */}
      <audio ref={audioRef} src="https://cdn.freesound.org/previews/263/263133_2064400-lq.mp3" preload="auto" />
      
      {/* Chat toggle button with notification badge */}
      <button 
        className="chat-toggle-button"
        onClick={toggleChat}
        aria-label={showChat ? "Hide chat" : "Show chat"}
      >
        <span className="chat-icon">💬</span>
        {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
      </button>

      {/* Chat panel */}
      {showChat && (
        <div className="chat-panel" ref={chatContainerRef}>
          <div className="chat-header">
            <h3>Game Chat</h3>
            <button 
              className="close-chat-button" 
              onClick={() => setShowChat(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div className="chat-messages">
            {isLoading ? (
              <p className="loading-message">Loading messages...</p>
            ) : messages.length === 0 ? (
              <p className="empty-message">No messages yet. Start the conversation!</p>
            ) : (
              messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`message ${msg.playerName === playerName ? 'own-message' : ''}`}
                >
                  <div className="message-header">
                    <span className="message-sender">{msg.playerName}</span>
                    <span className="message-time">{formatTimestamp(msg.timestamp)}</span>
                  </div>
                  <div className="message-content">{msg.message}</div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSendMessage} className="chat-input-container">
            <input
              ref={messageInputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              maxLength={200}
              aria-label="Chat message input"
            />
            <button 
              type="submit" 
              disabled={!newMessage.trim()}
              aria-label="Send message"
            >
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default GameChat; 